import { Box, Typography } from "@mui/material";
import { forwardRef, useMemo } from "react";
import { NumericFormat } from "react-number-format";

const borderW = 1;

function buildQuantityFieldSx(rowH: number, digitSize: number) {
  const innerH = rowH - borderW * 2;
  return {
    m: 0,
    p: 0,
    pl: 0.35,
    pr: 0.35,
    width: "100%",
    minWidth: 0,
    height: innerH,
    minHeight: innerH,
    maxHeight: innerH,
    boxSizing: "border-box" as const,
    border: "none",
    textAlign: "center" as const,
    fontSize: digitSize,
    fontWeight: 800,
    letterSpacing: "0.02em",
    color: "#0d0d0d",
    fontFamily: "inherit",
    lineHeight: `${innerH}px`,
    backgroundColor: "transparent",
    WebkitFontSmoothing: "antialiased",
    WebkitAppearance: "none" as const,
    appearance: "none" as const,
    display: "block",
    "&:focus": { outline: "none" },
  } as const;
}

function buildStepperButtonSx(rowH: number, glyphSize: number) {
  return {
    m: 0,
    font: "inherit",
    lineHeight: 0,
    WebkitTapHighlightColor: "transparent",
    width: rowH,
    height: rowH,
    minWidth: rowH,
    p: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box" as const,
    border: `${borderW}px solid #C4C4C4`,
    borderRadius: "50%",
    backgroundColor: "#fff",
    color: "#6a6a6a",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    fontSize: glyphSize,
    fontWeight: 500,
    letterSpacing: 0,
    userSelect: "none" as const,
    fontFamily: 'system-ui, "Segoe UI", Roboto, sans-serif',
    "&:hover": {
      backgroundColor: "#f6f6f5",
      borderColor: "#a8a8a8",
    },
    "&:active": { backgroundColor: "#ececea" },
    "&:focus-visible": {
      outline: "2px solid rgba(0, 127, 24, 0.45)",
      outlineOffset: 1,
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
      borderColor: "#e0e0e0",
    },
  } as const;
}

/** Desktop table: gọn để vừa cột ~23% (tránh Add sát/cắt mép phải) */
const quantityFieldSxDefault = buildQuantityFieldSx(32, 14);
const quantityFieldSxInline = buildQuantityFieldSx(28, 14);

const QuantityFormatInputDefault = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function QuantityFormatInputDefault(props, ref) {
  return (
    <Box component="input" ref={ref} {...props} sx={quantityFieldSxDefault} />
  );
});

const QuantityFormatInputInline = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function QuantityFormatInputInline(props, ref) {
  return (
    <Box component="input" ref={ref} {...props} sx={quantityFieldSxInline} />
  );
});

type QuickAddActionProps = {
  quantity: number;
  onQuantityChange: (value: number) => void;
  onAdd: () => void;
  canAdd: boolean;
  /** Một hàng cạnh tên (mobile list phẳng) */
  variant?: "default" | "inline";
};

export function QuickAddAction({
  quantity,
  onQuantityChange,
  onAdd,
  canAdd,
  variant = "default",
}: QuickAddActionProps) {
  const inline = variant === "inline";
  /** Inline: gọn hơn để nhường chỗ tên cây trên mobile */
  const rowH = inline ? 28 : 32;
  const glyphSize = inline ? 14 : 14;
  const gapPx = inline ? 6 : 6;
  const qtyMinW = inline ? 44 : 44;
  const qtyW = inline ? 64 : 68;
  const qtyMaxW = inline ? 88 : 96;
  const addLabelSize = inline ? 13 : 12;
  const addMinW = inline ? 32 : 0;

  const stepperButtonSx = useMemo(
    () => buildStepperButtonSx(rowH, glyphSize),
    [rowH, glyphSize]
  );

  const addTextSx = useMemo(
    () =>
      ({
        m: 0,
        p: 0,
        color: "#004d0e",
        fontWeight: 700,
        fontSize: addLabelSize,
        lineHeight: 1,
        letterSpacing: "0.01em",
        cursor: "pointer",
        userSelect: "none" as const,
        pl: inline ? 0.25 : 0.25,
        pr: 0,
        minWidth: addMinW || undefined,
        textAlign: "left" as const,
        display: "block",
        "&:hover": { color: "#004d0e" },
      }) as const,
    [addLabelSize, addMinW, inline]
  );

  return (
    <Box
      sx={{
        width: inline ? "auto" : { xs: "100%", sm: "23%" },
        flexShrink: inline ? 0 : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: inline ? "flex-end" : { xs: "flex-end", sm: "center" },
        paddingRight: inline ? 0.25 : { xs: 1.5, sm: 1.25 },
        paddingLeft: inline ? 0 : { sm: 0.25 },
        boxSizing: "border-box",
      }}>
      <Box
        role="group"
        aria-label="Quantity and add"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: `${gapPx}px`,
          minHeight: rowH,
          width: "max-content",
          maxWidth: "100%",
        }}>
        <Box
          component="button"
          type="button"
          disabled={quantity <= 0}
          aria-label="Decrease quantity"
          onClick={() => onQuantityChange(Math.max(0, quantity - 1))}
          sx={stepperButtonSx}>
          {/* ký tự trừ Unicode — cùng font với + */}
          −
        </Box>

        <Box
          sx={{
            minWidth: qtyMinW,
            width: qtyW,
            maxWidth: qtyMaxW,
            height: rowH,
            flexShrink: 0,
            boxSizing: "border-box",
            border: `${borderW}px solid #9a9a9a`,
            borderRadius: inline ? "6px" : "6px",
            bgcolor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            boxShadow: "inset 0 1px 1px rgba(0,0,0,0.04)",
          }}>
          <NumericFormat
            customInput={
              inline ? QuantityFormatInputInline : QuantityFormatInputDefault
            }
            value={Number.isNaN(quantity) ? 0 : quantity}
            onValueChange={(v) => {
              let n: number;
              if (v.floatValue != null && Number.isFinite(v.floatValue)) {
                n = v.floatValue;
              } else {
                const digits = String(v.value ?? "").replace(/[^\d]/g, "");
                n = digits === "" ? 0 : parseInt(digits, 10);
                if (!Number.isFinite(n)) n = 0;
              }
              onQuantityChange(
                Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(n)))
              );
            }}
            allowNegative={false}
            thousandSeparator=","
            decimalScale={0}
            isAllowed={(values) => {
              if (values.floatValue == null) return true;
              return values.floatValue <= Number.MAX_SAFE_INTEGER;
            }}
            inputMode="numeric"
            autoComplete="off"
            type="text"
            aria-label="Quantity"
          />
        </Box>

        <Box
          component="button"
          type="button"
          aria-label="Increase quantity"
          onClick={() => onQuantityChange(quantity + 1)}
          sx={stepperButtonSx}>
          +
        </Box>

        <Box
          onClick={onAdd}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onAdd();
            }
          }}
          role="button"
          tabIndex={0}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: rowH,
            flexShrink: 0,
            opacity: canAdd ? 1 : 0.55,
            cursor: "pointer",
            outline: "none",
            "&:focus-visible": {
              borderRadius: "4px",
              boxShadow: "0 0 0 2px rgba(0, 127, 24, 0.35)",
            },
          }}>
          <Typography component="span" variant="body2" sx={addTextSx}>
            Add
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
